import { CheckCircleFilled, CloseCircleFilled, RightOutlined, SearchOutlined } from "@ant-design/icons";
import { Input, Table, Tag, Tooltip } from "antd";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./AnalyticsPage.module.less";

const PASS_THRESHOLD = 60;

const AccuracyBar = ({ accuracy }) => {
  const color =
    accuracy >= 80 ? "#16a34a" :
    accuracy >= 60 ? "#4b93ff" :
    accuracy >= 40 ? "#d97706" : "#ef4444";

  return (
    <div className={styles.accuracyCell}>
      <div className={styles.accuracyBarTrack}>
        <div
          className={styles.accuracyBarFill}
          style={{ width: `${accuracy}%`, backgroundColor: color }}
        />
      </div>
      <span className={styles.accuracyPct} style={{ color }}>
        {accuracy}%
      </span>
    </div>
  );
};

const buildColumns = (nameLabel) => [
  {
    title: nameLabel,
    dataIndex: "name",
    key: "name",
    sorter: (a, b) => a.name.localeCompare(b.name),
    ellipsis: true,
    width: "26%",
    render: (v) => (
      <span className={styles.nameCell}>
        {v}
        <RightOutlined className={styles.drillIcon} />
      </span>
    ),
  },
  {
    title: "Correct",
    dataIndex: "correct",
    key: "correct",
    sorter: (a, b) => a.correct - b.correct,
    align: "center",
    width: "10%",
    render: (v) => (
      <span className={styles.correctCount}>
        <CheckCircleFilled style={{ color: "#16a34a", marginRight: 4 }} />
        {v}
      </span>
    ),
  },
  {
    title: "Incorrect",
    dataIndex: "incorrect",
    key: "incorrect",
    sorter: (a, b) => a.incorrect - b.incorrect,
    align: "center",
    width: "10%",
    render: (v) => (
      <span className={styles.incorrectCount}>
        <CloseCircleFilled style={{ color: "#ef4444", marginRight: 4 }} />
        {v}
      </span>
    ),
  },
  {
    title: "Total Qs",
    dataIndex: "total",
    key: "total",
    sorter: (a, b) => a.total - b.total,
    align: "center",
    width: "10%",
  },
  {
    title: "Accuracy",
    dataIndex: "accuracy",
    key: "accuracy",
    sorter: (a, b) => a.accuracy - b.accuracy,
    defaultSortOrder: "descend",
    width: "28%",
    render: (v) => <AccuracyBar accuracy={v} />,
  },
  {
    title: "Status",
    dataIndex: "accuracy",
    key: "status",
    align: "center",
    width: "16%",
    filters: [
      { text: "Passing", value: "pass" },
      { text: "Failing", value: "fail" },
    ],
    onFilter: (value, record) =>
      value === "pass"
        ? record.accuracy >= PASS_THRESHOLD
        : record.accuracy < PASS_THRESHOLD,
    render: (v) =>
      v >= PASS_THRESHOLD ? (
        <Tag color="success">Passing</Tag>
      ) : (
        <Tag color="error">Failing</Tag>
      ),
  },
];

const AnalyticsTable = ({ items = [], nameLabel = "Name", attribute = "specialty", isLoading }) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  const columns = useMemo(() => buildColumns(nameLabel), [nameLabel]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter((r) => r.name?.toLowerCase().includes(q));
  }, [items, search]);

  return (
    <div>
      <div className={styles.tableSearch}>
        <Input
          placeholder={`Search ${nameLabel.toLowerCase()}…`}
          prefix={<SearchOutlined />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          style={{ maxWidth: 320 }}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Tooltip title="Click any row to see a detailed breakdown">
            <span className={styles.drillHint}>Click a row to drill down</span>
          </Tooltip>
          <span className={styles.tableCount}>
            {filtered.length} {filtered.length === 1 ? "item" : "items"}
          </span>
        </div>
      </div>
      <Table
        dataSource={filtered}
        columns={columns}
        rowKey="name"
        loading={isLoading}
        size="middle"
        pagination={filtered.length > 10 ? { pageSize: 10 } : false}
        className={styles.analyticsTable}
        onRow={(record) => ({
          onClick: () => navigate(`/analytics/${attribute}/${encodeURIComponent(record.name)}`),
          style: { cursor: "pointer" },
        })}
      />
    </div>
  );
};

export default AnalyticsTable;
